import { useToast } from '@chakra-ui/react';
import { showToast } from '@diamond/shared/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { VOUCHER_QUERY_KEY, VOUCHER_TOAST_MESSAGES } from '../constants';
import type { VoucherStatus } from '../types';
import {
  useDeleteVoucherMutation,
  useUpdateVoucherDisplayMutation,
  useUpdateVoucherStatusMutation,
} from './api-hooks';

export const useVoucherMutations = () => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [deleteLoadingId, setDeleteLoadingId] = useState<string | null>(null);
  const [statusLoadingId, setStatusLoadingId] = useState<string | null>(null);
  const [displayLoadingId, setDisplayLoadingId] = useState<string | null>(null);

  const invalidateQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [VOUCHER_QUERY_KEY] });
  }, [queryClient]);

  const deleteMutation = useDeleteVoucherMutation({
    onSuccess: () => {
      showToast(toast, 'success', VOUCHER_TOAST_MESSAGES.deleteSuccess);
    },
    onError: (error: Error) => {
      showToast(toast, 'error', error.message);
    },
    onSettled: () => {
      setDeleteLoadingId(null);
    },
  });

  const updateStatusMutation = useUpdateVoucherStatusMutation({
    onSuccess: () => {
      showToast(toast, 'success', VOUCHER_TOAST_MESSAGES.statusUpdateSuccess);
    },
    onError: (error: Error) => {
      showToast(toast, 'error', error.message);
    },
    onSettled: () => {
      setStatusLoadingId(null);
    },
  });

  const updateDisplayMutation = useUpdateVoucherDisplayMutation({
    onSuccess: () => {
      showToast(toast, 'success', VOUCHER_TOAST_MESSAGES.displayUpdateSuccess);
    },
    onError: (error: Error) => {
      showToast(toast, 'error', error.message);
    },
    onSettled: () => {
      setDisplayLoadingId(null);
    },
  });

  const handleDelete = useCallback(
    (id: string) => {
      setDeleteLoadingId(id);
      deleteMutation.mutate(id);
    },
    [deleteMutation]
  );

  const handleStatusToggle = useCallback(
    (id: string, status: VoucherStatus) => {
      setStatusLoadingId(id);
      updateStatusMutation.mutate({ id, status });
    },
    [updateStatusMutation]
  );

  const handleDisplayToggle = useCallback(
    (id: string, isDisplay: boolean) => {
      setDisplayLoadingId(id);
      updateDisplayMutation.mutate({ id, isDisplay });
    },
    [updateDisplayMutation]
  );

  return {
    handleDelete,
    handleStatusToggle,
    handleDisplayToggle,
    deleteLoadingId,
    statusLoadingId,
    displayLoadingId,
    invalidateQueries,
  };
};
